import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useHotkeys } from '@/hooks/use-hotkeys';

export interface UseTableKeyboardOptions {
  containerRef: RefObject<HTMLElement | null>;
  rowCount: number;
  rowHeight: number;
  headerHeight: number;
  onOpen: (index: number) => void;
  onToggleSelect: (index: number) => void;
  onEscape: () => void;
  enabled?: boolean;
}

export interface TableKeyboardResult {
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
}

/**
 * The list-navigation contract of ARCHITECTURE §9: `j`/`k` move, `Enter` opens,
 * `x` selects, `Esc` drops the selection.
 *
 * The focused row is tracked by index rather than by DOM node because with
 * windowing on, the row you are moving towards may not be rendered yet: the
 * container is scrolled by arithmetic first, and focus follows once it exists.
 */
export function useTableKeyboard({
  containerRef,
  rowCount,
  rowHeight,
  headerHeight,
  onOpen,
  onToggleSelect,
  onEscape,
  enabled = true,
}: UseTableKeyboardOptions): TableKeyboardResult {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    if (focusedIndex >= rowCount) setFocusedIndex(rowCount - 1);
  }, [rowCount, focusedIndex]);

  const move = useCallback(
    (delta: number) => {
      setFocusedIndex((current) => {
        if (rowCount === 0) return -1;
        if (current === -1) return delta > 0 ? 0 : rowCount - 1;
        return Math.min(rowCount - 1, Math.max(0, current + delta));
      });
    },
    [rowCount],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || focusedIndex < 0) return;

    const top = focusedIndex * rowHeight;
    const visibleTop = container.scrollTop + headerHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (top < visibleTop) container.scrollTop = Math.max(0, top - headerHeight);
    else if (top + rowHeight > visibleBottom) {
      container.scrollTop = top + rowHeight - container.clientHeight;
    }

    // A jump past the overscan window lands on a row that has not been
    // rendered yet, and one frame is not always enough for the windowing pass
    // to catch up. Missing it drops focus to <body>, which silently kills every
    // scoped hotkey — so retry across a few frames instead of hoping.
    let frame = 0;
    let attempts = 0;
    const focusRow = () => {
      const row = container.querySelector<HTMLElement>(`[data-row-index="${focusedIndex}"]`);
      if (row) {
        row.focus({ preventScroll: true });
        // Y se corrige el desplazamiento con la posición REAL de la fila. La
        // aritmética de arriba supone que la fila número N empieza en
        // `N * rowHeight`, que es cierto mientras todas las filas midan lo
        // mismo; una tabla con un detalle abierto entre medio (`rowDetail`)
        // corre las de abajo. `nearest` no hace nada cuando la fila ya está
        // entera a la vista, que es el caso de siempre.
        row.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (attempts++ < 5) frame = requestAnimationFrame(focusRow);
    };
    frame = requestAnimationFrame(focusRow);
    return () => cancelAnimationFrame(frame);
  }, [focusedIndex, rowHeight, headerHeight, containerRef]);

  const activate = () => {
    if (focusedIndex >= 0) onOpen(focusedIndex);
  };

  const bindings: Record<string, () => void> = {
    j: () => move(1),
    k: () => move(-1),
    arrowdown: () => move(1),
    arrowup: () => move(-1),
    x: () => {
      if (focusedIndex >= 0) onToggleSelect(focusedIndex);
    },
    escape: () => {
      setFocusedIndex(-1);
      onEscape();
    },
  };

  useHotkeys(bindings, { enabled, scopeRef: containerRef });

  /** Every binding, plus the `Enter` the scoped listener no longer claims. */
  const latest = useRef<Record<string, () => void>>(bindings);
  latest.current = { ...bindings, enter: activate };

  /**
   * Enter, handed over to whatever inside the row actually holds focus.
   *
   * It cannot ride `useHotkeys`: that listener preventDefaults a bound key
   * before any handler can decline it, and its typing check exempts only
   * fields — so a focused row-actions button opened the record instead of its
   * menu, the completion toggle opened the record instead of ticking, and a
   * link in a cell was unreachable by keyboard entirely, since a link has no
   * other activation key (WCAG 2.1.1). Only a bare row hands Enter to the table.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return;

    const onEnter = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.defaultPrevented || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches('[data-row-index]')) return;
      event.preventDefault();
      latest.current.enter?.();
    };

    container.addEventListener('keydown', onEnter);
    return () => container.removeEventListener('keydown', onEnter);
  }, [enabled, containerRef]);

  /**
   * The same keys again, for the one state the scoped listener cannot see.
   *
   * Every navigation leaves focus on `<body>`, and there is no way to focus a
   * row with the mouse that does not also open it — so `j`, the first keystroke
   * of a keyboard-first flow, did nothing at all. This answers only while focus
   * is nowhere in particular, which is why it can swallow the key without
   * stealing `Enter` from a button or `Esc` from a dialog: anything else on
   * screen holds focus, and holding focus is what excludes it from here.
   */
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target !== document.body) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      const run = latest.current[event.key.toLowerCase()];
      if (!run) return;
      event.preventDefault();
      run();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);

  return { focusedIndex, setFocusedIndex };
}
